import React, { useState, useEffect } from "react";
import { X, Plus, Minus, Loader2, Gift } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { AnimatePresence, motion } from "framer-motion";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { GARDIENS } from "@/data/gardiens";

const FAMILIERS = GARDIENS.filter((g) => g.familiar).map((g) => ({
  value: `${g.familiar.name} — ${g.familiar.title}`,
  label: `${g.familiar.name}, ${g.familiar.title} (${g.name})`,
}));

const APP_OFFER_MIN = 100; // accès application offert dès 100 € d'achat, réservé aux particuliers

export const CartDrawer = () => {
  const { cart, cartOpen, setCartOpen, updateQty, removeItem, setItemFamiliar, cartTotal, clearCart, portalUser, setConnexionOpen, refreshPortal } = useApp();
  const [loading, setLoading] = useState(false);
  const [paypal, setPaypal] = useState({ enabled: false, client_id: "" });
  const [customerType, setCustomerType] = useState("particulier");

  useEffect(() => {
    api.get("/paypal/config").then((r) => setPaypal(r.data)).catch(() => {});
  }, []);

  const isConnected = !!portalUser;

  const requireLogin = () => {
    toast.info("Veuillez vous connecter pour finaliser votre commande.");
    setCartOpen(false);
    setConnexionOpen(true);
    return false;
  };

  const checkout = async () => {
    if (!isConnected) return requireLogin();
    setLoading(true);
    try {
      const items = cart.map((i) => ({ lookup_key: i.lookup_key, quantity: i.quantity, familiar: i.familiar || "" }));
      const { data } = await api.post("/payments/checkout", { items, origin_url: window.location.origin, customer_email: portalUser?.email || "", customer_type: customerType });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
      setLoading(false);
    }
  };

  const createPaypalOrder = async () => {
    const items = cart.map((i) => ({ lookup_key: i.lookup_key, quantity: i.quantity, familiar: i.familiar || "" }));
    const { data } = await api.post("/paypal/create-order", { items, origin_url: window.location.origin, customer_email: portalUser?.email || "", customer_type: customerType });
    return data.id;
  };
  const onPaypalApprove = async (data) => {
    try {
      const { data: res } = await api.post(`/paypal/capture-order/${data.orderID}`);
      if (res.payment_status === "paid") {
        clearCart();
        window.location.href = "/paiement/succes?paypal=1";
      } else {
        toast.error("Le paiement PayPal n'a pas été confirmé.");
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <AnimatePresence>
      {cartOpen && (
        <>
          <motion.div
            className="fixed inset-0 bg-foreground/40 backdrop-blur-sm z-[60]"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => setCartOpen(false)}
          />
          <motion.aside
            data-testid="cart-drawer"
            className="fixed top-0 right-0 h-full w-full max-w-md bg-background z-[70] flex flex-col shadow-2xl"
            initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }}
            transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
          >
            <div className="flex items-center justify-between px-7 py-6 border-b border-border">
              <h3 className="font-serif-display text-2xl">Votre panier</h3>
              <button onClick={() => setCartOpen(false)} data-testid="close-cart-btn" className="p-2 hover:bg-accent rounded-full transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-7 py-6 space-y-6">
              {cart.length === 0 && <p className="text-muted-foreground text-center mt-16">Votre panier est vide.</p>}
              {cart.map((i) => (
                <div key={i.lookup_key} className="flex gap-4" data-testid={`cart-item-${i.lookup_key}`}>
                  <img src={i.image} alt={i.name} className="w-20 h-24 object-cover rounded-xl" />
                  <div className="flex-1">
                    <p className="font-medium leading-tight">{i.name}</p>
                    <p className="text-sm text-muted-foreground mt-1">{i.price.toFixed(2)} €</p>
                    <div className="flex items-center gap-3 mt-3">
                      <button onClick={() => updateQty(i.lookup_key, i.quantity - 1)} className="p-1 border border-border rounded-full hover:bg-accent transition-colors"><Minus className="w-3 h-3" /></button>
                      <span className="text-sm w-5 text-center">{i.quantity}</span>
                      <button onClick={() => updateQty(i.lookup_key, i.quantity + 1)} className="p-1 border border-border rounded-full hover:bg-accent transition-colors"><Plus className="w-3 h-3" /></button>
                      <button onClick={() => removeItem(i.lookup_key)} className="ml-auto text-xs text-muted-foreground hover:text-destructive transition-colors">Retirer</button>
                    </div>
                    <label className="block mt-3">
                      <span className="text-[11px] uppercase tracking-[0.12em] text-warm">Familier à graver (offert)</span>
                      <select
                        value={i.familiar || ""}
                        onChange={(e) => setItemFamiliar(i.lookup_key, e.target.value)}
                        data-testid={`cart-familiar-${i.lookup_key}`}
                        className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-xs focus:outline-none focus:ring-1 focus:ring-warm"
                      >
                        <option value="">Sans familier</option>
                        {FAMILIERS.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
                      </select>
                    </label>
                  </div>
                </div>
              ))}
            </div>

            {cart.length > 0 && (
              <div className="border-t border-border px-7 py-6 space-y-4">
                <div data-testid="cart-app-offer" className="rounded-xl bg-warm/15 border border-warm/40 px-4 py-3 flex items-center gap-3">
                  <Gift className="w-4 h-4 text-warm shrink-0" strokeWidth={2} />
                  <p className="text-xs text-foreground/80">
                    {customerType === "professionnel" ? (
                      <>L'accès illimité à l'application est une offre <strong className="text-foreground">réservée aux particuliers</strong>.</>
                    ) : cartTotal >= APP_OFFER_MIN ? (
                      <><strong className="text-foreground">Offert :</strong> accès illimité à notre application de jeux et d'accompagnement cognitif.</>
                    ) : (
                      <><strong className="text-foreground">Plus que {(APP_OFFER_MIN - cartTotal).toFixed(2)} €</strong> pour recevoir l'accès illimité à l'application en cadeau (offre réservée aux particuliers).</>
                    )}
                  </p>
                </div>
                <div data-testid="customer-type-select">
                  <p className="text-[11px] uppercase tracking-[0.12em] text-warm mb-2">Vous commandez en tant que</p>
                  <div className="grid grid-cols-2 gap-2">
                    {[{ v: "particulier", l: "Particulier" }, { v: "professionnel", l: "Professionnel / établissement" }].map((o) => (
                      <button key={o.v} type="button" onClick={() => setCustomerType(o.v)} data-testid={`customer-type-${o.v}`}
                        className={`rounded-xl border px-3 py-2.5 text-xs transition-colors ${customerType === o.v ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-accent"}`}>
                        {o.l}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="flex justify-between text-lg">
                  <span className="text-muted-foreground">Sous-total</span>
                  <span data-testid="cart-total" className="font-serif-display text-2xl">{cartTotal.toFixed(2)} €</span>
                </div>
                <p className="text-xs text-muted-foreground">Taxes et livraison calculées au paiement.</p>
                {isConnected && (
                  <p className="text-xs text-muted-foreground text-center" data-testid="checkout-account-hint">
                    Vous commandez en tant que <strong className="text-foreground/80">{portalUser.email}</strong>
                  </p>
                )}
                {!isConnected && (
                  <button onClick={requireLogin} data-testid="cart-login-prompt" className="w-full rounded-xl bg-accent/60 border border-warm/40 text-foreground/80 py-3 text-sm hover:bg-accent transition-colors">
                    🔒 Connectez-vous ou créez un compte pour commander
                  </button>
                )}
                <button
                  data-testid="checkout-btn"
                  onClick={checkout}
                  disabled={loading}
                  className="w-full rounded-full bg-primary text-primary-foreground py-4 hover:scale-[1.02] transition-transform duration-300 flex items-center justify-center gap-2 disabled:opacity-60"
                >
                  {loading && <Loader2 className="w-4 h-4 animate-spin" />}
                  Payer par carte (Stripe)
                </button>

                {paypal.enabled && isConnected ? (
                  <div data-testid="paypal-button-container">
                    <div className="flex items-center gap-3 my-1">
                      <span className="h-px flex-1 bg-border" />
                      <span className="text-xs text-muted-foreground">ou</span>
                      <span className="h-px flex-1 bg-border" />
                    </div>
                    <PayPalScriptProvider options={{ clientId: paypal.client_id, currency: "EUR", intent: "capture" }}>
                      <PayPalButtons
                        style={{ layout: "horizontal", color: "gold", shape: "pill", tagline: false, height: 48 }}
                        createOrder={createPaypalOrder}
                        onApprove={onPaypalApprove}
                        onError={() => toast.error("Une erreur PayPal est survenue.")}
                      />
                    </PayPalScriptProvider>
                  </div>
                ) : (
                  <p className="text-[11px] text-muted-foreground text-center" data-testid="paypal-disabled-note">
                    PayPal sera disponible une fois configuré.
                  </p>
                )}
              </div>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
};
